//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, InputAdornment, InputLabel, TextField, Typography, CircularProgress } from '@mui/material'
import {
  commaDecStringToBigNumberWei,
  commaDecStringToErc20Wei,
  commaDecStringToErcDecString,
  erc20WeiToCommaDecString,
  erc20WeiToErc20DecString,
  erc20WeiToNumber,
  toCommaDecString,
} from '@shared/conversions'
import { PoolFlexAccountCTO, PoolFlexOverviewCTO } from '@shared/sharedTypes'
import React, { ReactElement } from 'react'
import ButtonAction from '@react/components/elements/ButtonAction'
import { poolActions } from '@react/redux/poolSlice'

import { PoolTypeEnum } from '@shared/Enums'

import { useOtDispatch } from '@react/redux/store'
import { redeemRequestFromPoolTx } from '@react/signedTxs/redeemRequestFromPoolTx'
import { useModalForm } from '@react/UseModalForm'
import { logOT } from '@shared/loggingShared'
import { textFieldValueToBigNumberWei } from '@react/utils/textFieldsUtils'

export default function LenderRequestWithdrawFlexForm(props: {
  poolFlexOverviewCTO: PoolFlexOverviewCTO
  poolFlexAccountCTO: PoolFlexAccountCTO
}): ReactElement {
  const { poolFlexOverviewCTO, poolFlexAccountCTO } = props

  const dispatch = useOtDispatch()
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const exchangeRateBN = BigInt(poolFlexOverviewCTO?.exchangeRate ?? 1)
  const liquidityTokenSymbol = poolFlexOverviewCTO.liquidityTokenSymbol

  const initialUsdcBN = (BigInt(poolFlexAccountCTO?.maxRedeemRequest) * exchangeRateBN) / BigInt(1e18)

  const initialUSDC = Math.round(Number(initialUsdcBN) / 1e4) / 100

  const [valueUSDC, setValueUSDC] = React.useState<string>(initialUSDC.toLocaleString(navigator.language))
  const [valuePPT, setValuePPT] = React.useState<string>(erc20WeiToErc20DecString(poolFlexAccountCTO?.maxRedeemRequest))
  const [errorUSDC, setErrorUSDC] = React.useState<string | undefined>(undefined)
  const [errorPPT, setErrorPPT] = React.useState<string | undefined>(undefined)
  useModalForm({ poolEventToCloseOn: poolActions.redeemRequestFromPoolSuccess.type })

  const maxPPTBn = BigInt(poolFlexAccountCTO?.maxRedeemRequest)
  const maxPPTString = erc20WeiToCommaDecString(poolFlexAccountCTO?.maxRedeemRequest)
  const maxUSDCBn = (BigInt(poolFlexAccountCTO?.maxRedeemRequest) * exchangeRateBN) / BigInt(1e18)
  const maxUSDCString = erc20WeiToCommaDecString(maxUSDCBn)

  const handleWithdrawRequest = async (): Promise<void> => {
    try {
      setIsLoading(true)
      logOT.debug('LenderRequestWithdrawForm handleWithdrawRequest', valuePPT, valueUSDC)
      await dispatch(
        redeemRequestFromPoolTx({
          poolAddr: poolFlexOverviewCTO.poolAddr,
          sharesInWei: commaDecStringToErc20Wei(valuePPT),
          poolType: PoolTypeEnum.FlexibleRate,
        })
      )
      const event = new Event('modalClose')
      window.dispatchEvent(event)
    } catch (error) {
      logOT.error('Error in handleWithdrawRequest', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const errorCheck = (ppt: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', ppt)

    if (ppt > BigInt(poolFlexAccountCTO?.maxRedeemRequest)) {
      logOT.debug('LenderRequestWithdrawForm Error ', ppt, erc20WeiToNumber(poolFlexAccountCTO?.maxRedeemRequest))

      setErrorUSDC(
        `Amount exceeds max withdraw request  ${erc20WeiToCommaDecString(
          poolFlexAccountCTO?.maxWithdrawRequest
        )} ${liquidityTokenSymbol}`
      )
      setErrorPPT(
        `Amount exceeds max redemption request  ${erc20WeiToCommaDecString(poolFlexAccountCTO?.maxRedeemRequest)} ${
          poolFlexOverviewCTO?.symbol
        }`
      )
    } else {
      setErrorPPT(undefined)
      setErrorUSDC(undefined)
    }
  }

  const handleOnFocusUSDC = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocus', valueUSDC)

    setValueUSDC(toCommaDecString(valueUSDC))

    logOT.info('handleOnFocusUSDC1', valueUSDC)
    const usdc = commaDecStringToBigNumberWei(valueUSDC)

    let ppt
    if (Math.round(Number(usdc) / 10000) == Math.round(Number(maxUSDCBn) / 10000)) {
      ppt = maxPPTBn
    } else {
      ppt = (usdc * BigInt(1e18)) / exchangeRateBN
    }
    logOT.info('handleOnFocusUSDC2', usdc, maxUSDCBn, ppt)

    setValuePPT(erc20WeiToCommaDecString(ppt))
    errorCheck(ppt)
  }
  const handleOnBlurUSDC = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', valueUSDC)
    setValueUSDC(toCommaDecString(valueUSDC))
  }
  const handleOnChangeUSDC = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)
    setValueUSDC(event.target.value)
    const usdc = textFieldValueToBigNumberWei(event)

    let ppt
    if (Math.round(Number(usdc) / 10000) == Math.round(Number(maxUSDCBn) / 10000)) {
      ppt = maxPPTBn
    } else {
      ppt = (usdc * BigInt(1e18)) / exchangeRateBN
    }

    setValuePPT(erc20WeiToCommaDecString(ppt))
    errorCheck(ppt)
  }

  const handleOnFocusPPT = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocusPPT1', valuePPT)
    const ppt = commaDecStringToBigNumberWei(valuePPT)
    setValuePPT(commaDecStringToErcDecString(valuePPT))

    const usdc = (ppt * exchangeRateBN) / BigInt(1e18)
    setValueUSDC(erc20WeiToCommaDecString(usdc))
    errorCheck(ppt)
  }
  const handleOnBlurPPT = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', valuePPT)
    setValuePPT(toCommaDecString(valuePPT))
  }
  const handleOnChangePPT = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)

    setValuePPT(event.target.value)
    const ppt = textFieldValueToBigNumberWei(event)
    const usdc = (ppt * exchangeRateBN) / BigInt(1e18)

    setValueUSDC(erc20WeiToCommaDecString(usdc))
    errorCheck(ppt)
  }

  const handleMaxClickUSDC = (): void => {
    setValueUSDC(maxUSDCString)
    setValuePPT(maxPPTString)
    errorCheck(maxPPTBn)
  }

  const disableButton = !!errorUSDC || !!errorPPT || isLoading
  logOT.debug('LenderRequestWithdrawForm errorUSDC', disableButton, errorUSDC, errorPPT)

  return (
    <>
      <Box py={2} px={2}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Typography variant="bodyBold" margin="dense">
              {` Withdraw Request- ${erc20WeiToCommaDecString(
                poolFlexAccountCTO?.maxWithdrawRequest
              )} ${liquidityTokenSymbol} available to withdraw`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="bodyBold" margin="dense">
              {`Redeem Request - ${erc20WeiToCommaDecString(poolFlexAccountCTO?.maxRedeemRequest)} ${
                poolFlexOverviewCTO.symbol
              } available to Redeem`}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body" margin={'dense'}>
              Please input the amount that you would like to withdraw.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="amount">Withdraw Request ({liquidityTokenSymbol})</InputLabel>
            <TextField
              id="amount"
              data-testid="amount-usdc"
              InputProps={{
                endAdornment: <InputAdornment position="start">{liquidityTokenSymbol}</InputAdornment>,
              }}
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Withdraw Amount"
              value={valueUSDC}
              type={'tel'}
              onFocus={handleOnFocusUSDC}
              onChange={handleOnChangeUSDC}
              onBlur={handleOnBlurUSDC}
              error={!!errorUSDC}
              helperText={errorUSDC}
            />
            <Box sx={{ width: '300px', display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button
                data-testid="button_max"
                color="secondary"
                variant="outlined"
                sx={{
                  padding: '4px 8px',
                  textTransform: 'none',
                }}
                onClick={handleMaxClickUSDC}
              >
                Max
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="ppt">Withdraw Request ({poolFlexOverviewCTO.symbol})</InputLabel>
            <TextField
              id="ppt"
              data-testid="amount-ppt"
              InputProps={{
                endAdornment: <InputAdornment position="start">{poolFlexOverviewCTO.symbol}</InputAdornment>,
              }}
              inputProps={{
                'data-testid': 'ppt',
              }}
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Withdraw Amount"
              value={valuePPT}
              type={'tel'}
              onFocus={handleOnFocusPPT}
              onChange={handleOnChangePPT}
              onBlur={handleOnBlurPPT}
              error={!!errorPPT}
              helperText={errorPPT}
            />
            <Box sx={{ width: '300px', display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button
                data-testid="button_max-ppt"
                color="secondary"
                variant="outlined"
                sx={{
                  padding: '4px 8px',
                  textTransform: 'none',
                }}
                onClick={handleMaxClickUSDC}
              >
                Max
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ width: '300px' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <ButtonAction
                data-testid={'WithdrawRequest'}
                disabled={disableButton}
                sx={{ marginRight: '16px' }}
                variant={'primary'}
                onClick={handleWithdrawRequest}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    <Typography>Processing...</Typography>
                  </Box>
                ) : (
                  'Withdraw Request'
                )}
              </ButtonAction>

              <ButtonAction variant={'tertiary'} onClick={handleCancel} disabled={isLoading}>
                Cancel
              </ButtonAction>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
